import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Timer, ArrowLeft, ArrowRight, Loader2, CheckCircle2, X,
  Trophy, RotateCcw, Volume2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { AmbientCanvas } from "@/components/effects/ambient-canvas";
import { PageFrame } from "@/components/page-frame";
import { AppTopBar } from "@/components/app-topbar";
import {
  fetchAttemptedVocab, getRecentTopicId, fetchTopicInfo,
  saveProgress, submitQuizSession,
  type VocabWord, type Topic
} from "@/lib/queries";
import { playWordClick, speakWord } from "@/lib/audio";
import { playCorrect, playWrong, playReveal, playSessionEnd, playTick } from "@/lib/sounds";
import { flyStarsToPill, confettiForResult } from "@/lib/effects";
import { cn } from "@/lib/utils";

const SECONDS_PER_QUESTION = 8;
const MAX_QUESTIONS = 20;
const MIN_VOCAB_FOR_OPTIONS = 6;
const COUNTDOWN_FROM = 3;

type Phase = "loading" | "no-vocab" | "countdown" | "playing" | "results";
type Direction = "ar-en" | "en-ar";
type Option = { id: string; label: string; lang: "en" | "ar"; isCorrect: boolean };
type Question = { word: VocabWord; direction: Direction; options: Option[] };

export default function SpeedReview() {
  const nav = useNavigate();
  const [phase, setPhase] = React.useState<Phase>("loading");
  const [topic, setTopic] = React.useState<Topic | null>(null);
  const [, setPool]   = React.useState<VocabWord[]>([]);
  const [topicWidened, setWidened] = React.useState(false);

  const [questions, setQs] = React.useState<Question[]>([]);
  const [idx, setIdx]      = React.useState(0);
  const [picked, setPicked] = React.useState<string | null>(null);
  const [correct, setCorrect] = React.useState(0);
  const [timeLeft, setTimeLeft] = React.useState(SECONDS_PER_QUESTION);
  const [countdownN, setCountdownN] = React.useState(COUNTDOWN_FROM);

  const startRef    = React.useRef<Date>(new Date());
  const tickRef     = React.useRef<number | null>(null);
  const questionShownAtRef = React.useRef<number>(0);

  const [submitting, setSubmitting] = React.useState(false);
  const [awarded, setAwarded] = React.useState<{ new_total: number; points_awarded: number } | null>(null);

  // Bootstrap.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase("loading");
      const recentTopicId = getRecentTopicId();

      let attempted = await fetchAttemptedVocab(recentTopicId ? { topicId: recentTopicId } : undefined);
      let widened = false;
      if (attempted.length < MIN_VOCAB_FOR_OPTIONS && recentTopicId) {
        attempted = await fetchAttemptedVocab();
        widened = true;
      }
      if (cancelled) return;
      setPool(attempted);
      setWidened(widened);

      if (recentTopicId && !widened) {
        const info = await fetchTopicInfo(recentTopicId);
        if (!cancelled && info) setTopic(info);
      }

      if (attempted.length < MIN_VOCAB_FOR_OPTIONS) { setPhase("no-vocab"); return; }

      // Build the question list once.
      const picks = shuffle([...attempted]).slice(0, Math.min(MAX_QUESTIONS, attempted.length));
      const qs = picks.map(w => buildQuestion(w, attempted));
      setQs(qs);
      setIdx(0);
      setCorrect(0);
      startRef.current = new Date();

      // Run the 3-2-1 countdown, then go.
      setCountdownN(COUNTDOWN_FROM);
      setPhase("countdown");
    })();
    return () => { cancelled = true; };
  }, []);

  // Countdown effect.
  React.useEffect(() => {
    if (phase !== "countdown") return;
    if (countdownN <= 0) {
      setPhase("playing");
      return;
    }
    const t = window.setTimeout(() => setCountdownN(n => n - 1), 800);
    return () => window.clearTimeout(t);
  }, [phase, countdownN]);

  // Per-question timer.
  React.useEffect(() => {
    if (phase !== "playing") return;
    setTimeLeft(SECONDS_PER_QUESTION);
    setPicked(null);
    questionShownAtRef.current = Date.now();
    const q = questions[idx];
    playReveal();
    if (q?.direction === "ar-en") void speakWord(q.word);

    if (tickRef.current) window.clearInterval(tickRef.current);
    tickRef.current = window.setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          window.clearInterval(tickRef.current!);
          if (!picked) onTimeout();
          return 0;
        }
        if (t <= 3) playTick();
        return t - 1;
      });
    }, 1000) as unknown as number;

    return () => { if (tickRef.current) window.clearInterval(tickRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, idx]);

  function onTimeout() {
    const q = questions[idx];
    if (!q) return;
    void saveProgress({
      word_id: q.word.id, correct: false, mode: "speed",
      timeMs:  SECONDS_PER_QUESTION * 1000
    });
    playWrong();
    window.setTimeout(advance, 500);
  }

  function pickAnswer(opt: Option, btn: HTMLElement | null) {
    if (picked) return;
    setPicked(opt.id);
    const q = questions[idx];
    if (!q) return;
    const timeMs = Date.now() - questionShownAtRef.current;
    void saveProgress({ word_id: q.word.id, correct: opt.isCorrect, mode: "speed", timeMs });
    if (opt.isCorrect) {
      setCorrect(c => c + 1);
      playCorrect();
      flyStarsToPill(btn);
    } else {
      playWrong();
    }
    if (tickRef.current) window.clearInterval(tickRef.current);
    window.setTimeout(advance, 500);
  }

  function advance() {
    const ni = idx + 1;
    if (ni >= questions.length) { void endRound(); return; }
    setIdx(ni);
  }

  async function endRound() {
    setPhase("results");
    setSubmitting(true);
    playSessionEnd(correct, questions.length);
    window.setTimeout(() => {
      const card = document.querySelector("[data-results-card]");
      confettiForResult({ correct, total: questions.length, originEl: card });
    }, 80);
    try {
      const r = await submitQuizSession({
        claimed_correct: correct,
        session_started: startRef.current,
        mode:            "speed"
      });
      if (r) setAwarded(r);
    } catch (e) { console.warn("[speed] submit", e); }
    setSubmitting(false);
  }

  const q = questions[idx];
  const progressPct = questions.length > 0 ? (idx / questions.length) * 100 : 0;
  const topicLabel = topic ? topic.name_en : topicWidened ? "Mixed topics" : "Practised words";

  return (
    <PageFrame surface="app">
      <AppTopBar />
      <div className="relative">
        <AmbientCanvas className="absolute inset-0 -z-10 w-full h-[900px] pointer-events-none" density={40} />

        <div className="relative mx-auto max-w-3xl px-4 md:px-6 pt-10 pb-32">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard"><ArrowLeft className="w-4 h-4" /> Back</Link>
            </Button>
            {phase === "playing" && <Badge variant="accent">Q {idx + 1} / {questions.length}</Badge>}
          </div>

          <BlurReveal>
            <div className="flex items-center gap-3 mb-2">
              <Timer className="w-6 h-6 text-accent" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Speed Review</h1>
            </div>
            <p className="text-fg-2">
              <span className="text-accent">{topicLabel}</span>
              <span className="text-fg-3"> · 8 sec per question · up to 15 pts each</span>
            </p>
          </BlurReveal>

          {phase === "playing" && (
            <div className="mt-4 grid grid-cols-[1fr_auto] gap-4 items-center">
              <div className="h-1.5 rounded-full bg-bg-2 overflow-hidden">
                <div className="h-full bg-accent transition-all" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-10 h-10 rounded-full border-2 inline-flex items-center justify-center text-sm font-bold tabular-nums",
                  timeLeft <= 2 ? "border-bad text-bad" : timeLeft <= 4 ? "border-accent text-accent" : "border-line text-fg-2"
                )}>{timeLeft}</div>
              </div>
            </div>
          )}

          <div className="mt-8">
            {phase === "loading" && <Spinner />}

            {phase === "no-vocab" && (
              <Card className="text-center"><CardContent className="py-12">
                <p className="text-fg-2">You need at least {MIN_VOCAB_FOR_OPTIONS} practised words to run Speed Review.</p>
                <Button className="mt-4" asChild><Link to="/learn">Practise a topic first</Link></Button>
              </CardContent></Card>
            )}

            {phase === "countdown" && (
              <BlurReveal>
                <Card className="text-center">
                  <CardContent className="py-20">
                    <div className="text-xs uppercase tracking-widest text-fg-3 mb-4">Get ready</div>
                    <div className="text-8xl md:text-9xl font-bold text-accent">
                      {countdownN === 0 ? "GO" : countdownN}
                    </div>
                  </CardContent>
                </Card>
              </BlurReveal>
            )}

            {phase === "playing" && q && (
              <BlurReveal>
                <Card className="text-center">
                  <CardContent className="py-10">
                    <div className="text-xs uppercase tracking-widest text-fg-3">
                      {q.direction === "ar-en" ? "What does this mean?" : "What's the Arabic for…"}
                    </div>
                    <div className={cn(
                      "mt-5 font-bold text-accent",
                      q.direction === "ar-en" ? "text-6xl md:text-7xl" : "text-4xl md:text-5xl"
                    )} style={q.direction === "ar-en"
                      ? { fontFamily: "'Noto Sans Arabic',serif", direction: "rtl" }
                      : undefined}>
                      {q.direction === "ar-en" ? q.word.arabic : q.word.english}
                    </div>
                    {q.direction === "ar-en" && (
                      <Button variant="ghost" size="sm" className="mt-3" onClick={() => playWordClick(q.word)}>
                        <Volume2 className="w-4 h-4" /> Listen
                      </Button>
                    )}

                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {q.options.map((opt, i) => {
                        const isPicked = picked === opt.id;
                        const showRight = picked !== null && opt.isCorrect;
                        const showWrong = isPicked && !opt.isCorrect;
                        return (
                          <button key={opt.id} type="button"
                            disabled={picked !== null}
                            onClick={e => pickAnswer(opt, e.currentTarget)}
                            className={cn(
                              "rounded-2xl border px-5 py-4 text-left transition-colors flex items-start gap-3",
                              !picked && "border-line bg-bg hover:bg-bg-3 hover:border-line-2",
                              showRight && "border-ok/40 bg-ok/10 text-ok",
                              showWrong && "border-bad/40 bg-bad/10 text-bad",
                              picked && !showRight && !showWrong && "border-line bg-bg-3/30 text-fg-2 opacity-60"
                            )}
                            style={opt.lang === "ar" ? { fontFamily: "'Noto Sans Arabic',serif", direction: "rtl" } : undefined}>
                            <span className="text-xs uppercase tracking-widest text-fg-3 shrink-0">{String.fromCharCode(65 + i)}</span>
                            <span className={cn("flex-1", opt.lang === "ar" && "text-lg")}>{opt.label}</span>
                            {showRight && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                            {showWrong && <X className="w-4 h-4 shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </BlurReveal>
            )}

            {phase === "results" && (
              <BlurReveal>
                <Card data-results-card>
                  <CardContent className="text-center py-10">
                    <div className="text-xs uppercase tracking-widest text-fg-3">Round complete</div>
                    <h2 className="text-4xl font-bold tracking-tight mt-2">
                      {correct} / {questions.length} <span className="text-fg-3 font-normal">correct</span>
                    </h2>

                    {submitting ? (
                      <p className="mt-4 text-fg-2 inline-flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-accent" /> Banking points…
                      </p>
                    ) : awarded ? (
                      <div className="mt-5 inline-flex items-center gap-3 rounded-2xl border border-accent-lo bg-accent/10 px-5 py-3">
                        <Trophy className="w-5 h-5 text-accent" />
                        <span className="text-accent font-bold text-xl">+{awarded.points_awarded.toLocaleString()}</span>
                        <span className="text-xs uppercase tracking-widest text-fg-3">points</span>
                        <span className="text-xs text-fg-2">· new total {awarded.new_total.toLocaleString()}</span>
                      </div>
                    ) : (
                      <p className="mt-4 text-fg-2 text-sm">Points couldn't sync — try again in a moment.</p>
                    )}

                    <div className="mt-7 flex flex-wrap gap-3 justify-center">
                      <Button size="lg" onClick={() => nav(0)}><RotateCcw className="w-4 h-4" /> Run again</Button>
                      <Button variant="outline" size="lg" asChild><Link to="/dashboard">Dashboard <ArrowRight className="w-4 h-4" /></Link></Button>
                    </div>
                  </CardContent>
                </Card>
              </BlurReveal>
            )}
          </div>
        </div>
      </div>
    </PageFrame>
  );
}

function Spinner() {
  return (
    <div className="py-16 flex items-center justify-center gap-2 text-fg-3">
      <Loader2 className="w-4 h-4 animate-spin text-accent" /> Loading…
    </div>
  );
}

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildQuestion(word: VocabWord, pool: VocabWord[]): Question {
  const direction: Direction = Math.random() < 0.5 ? "ar-en" : "en-ar";
  const lang: "en" | "ar" = direction === "ar-en" ? "en" : "ar";
  const field = direction === "ar-en" ? "english" : "arabic";
  const distractors = shuffle(pool.filter(w => w.id !== word.id)).slice(0, 3);
  const all = [word, ...distractors];
  const options: Option[] = shuffle(all.map(w => ({
    id:        String(w.id),
    label:     (w as any)[field] || "—",
    lang,
    isCorrect: w.id === word.id
  })));
  return { word, direction, options };
}
